/**
 * Wallet Selector Component
 *
 * Modal that displays discovered browser wallets (via EIP-6963)
 * and handles wallet connection for authentication.
 */

import { useState, useEffect, useCallback } from 'react'
import { Modal } from './Modal'
import {
  initWalletDiscovery,
  getDiscoveredWallets,
  onWalletsDiscovered,
  isSmartContractWallet,
} from '../services/wallet/discovery'
import { createFairdropSIWEMessage, verifySIWESignature } from '../services/wallet/siwe'
import { logger } from '../utils/logger'
import type { DetectedWallet, EIP1193Provider } from '../services/wallet/types'

export interface WalletAuthResult {
  address: string
  signature: string
  chainId: number
  isSmartContractWallet: boolean
  providerName: string
  providerRdns: string
  provider: EIP1193Provider
}

interface WalletSelectorProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (result: WalletAuthResult) => void
  onError?: (error: Error) => void
}

type ConnectionState = 'idle' | 'connecting' | 'signing' | 'verifying' | 'error'

export function WalletSelector({
  isOpen,
  onClose,
  onSuccess,
  onError,
}: WalletSelectorProps) {
  const [wallets, setWallets] = useState<DetectedWallet[]>([])
  const [selectedWallet, setSelectedWallet] = useState<DetectedWallet | null>(null)
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle')
  const [error, setError] = useState<string | null>(null)

  // Initialize wallet discovery on mount
  useEffect(() => {
    if (isOpen) {
      initWalletDiscovery()
      setWallets(getDiscoveredWallets())

      const unsubscribe = onWalletsDiscovered((discovered) => {
        setWallets(discovered)
      })

      return () => unsubscribe()
    }
  }, [isOpen])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedWallet(null)
      setConnectionState('idle')
      setError(null)
    }
  }, [isOpen])

  const handleConnect = useCallback(async (wallet: DetectedWallet) => {
    setSelectedWallet(wallet)
    setConnectionState('connecting')
    setError(null)

    try {
      const provider = wallet.provider

      // For MetaMask and similar wallets, request permissions first
      // This forces the wallet to show the account picker
      try {
        await provider.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }],
        })
      } catch (permErr) {
        // wallet_requestPermissions may not be supported by all wallets
        // or user may cancel - that's ok, we'll try eth_requestAccounts
        logger.debug('[WalletSelector] wallet_requestPermissions:', permErr)
      }

      // Request account access
      const accounts = await provider.request({
        method: 'eth_requestAccounts',
      }) as string[]

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found')
      }

      const address = accounts[0]

      // Get chain ID
      const chainIdHex = await provider.request({
        method: 'eth_chainId',
      }) as string
      const chainId = parseInt(chainIdHex, 16)

      // Check if smart contract wallet
      const isScw = await isSmartContractWallet(address, provider)
      if (isScw) {
        logger.debug('[WalletSelector] Smart contract wallet detected:', wallet.info.name)
      }

      // Create and sign SIWE message
      setConnectionState('signing')
      const { message } = await createFairdropSIWEMessage(address, chainId)

      const signature = await provider.request({
        method: 'personal_sign',
        params: [message, address],
      }) as string

      // Verify signature
      setConnectionState('verifying')
      const verification = await verifySIWESignature(message, signature, address)

      if (!verification.success) {
        throw new Error(verification.error || 'Signature verification failed')
      }

      // Success!
      onSuccess({
        address,
        signature,
        chainId,
        isSmartContractWallet: isScw,
        providerName: wallet.info.name,
        providerRdns: wallet.info.rdns,
        provider,
      })

      onClose()
    } catch (err) {
      logger.error('[WalletSelector] Connection error:', err)

      // Extract error message from various error formats
      let errorMessage = 'Connection failed'
      if (err instanceof Error) {
        errorMessage = err.message
      } else if (typeof err === 'object' && err !== null) {
        // Handle wallet RPC errors which may have nested structure
        const rpcError = err as { message?: string; reason?: string; code?: number }
        errorMessage = rpcError.message || rpcError.reason || JSON.stringify(err)
      } else if (typeof err === 'string') {
        errorMessage = err
      }

      // Handle user rejection - close modal and return to login
      if (
        errorMessage.includes('User rejected') ||
        errorMessage.includes('User denied') ||
        errorMessage.includes('user rejected') ||
        errorMessage.includes('Rejected')
      ) {
        onClose()
        return
      }

      setError(errorMessage)
      setConnectionState('error')
      onError?.(err instanceof Error ? err : new Error(errorMessage))
    }
  }, [onSuccess, onError, onClose])

  const getStatusText = () => {
    switch (connectionState) {
      case 'connecting':
        return 'Connecting to wallet...'
      case 'signing':
        return 'Please sign the message in your wallet...'
      case 'verifying':
        return 'Verifying signature...'
      default:
        return null
    }
  }

  // Placeholder icon for wallets without icons
  const WalletIcon = ({ icon, name }: { icon: string; name: string }) => {
    if (icon && icon.startsWith('data:')) {
      return <img src={icon} alt={name} className="wallet-icon" />
    }
    // Fallback icon
    return (
      <div className="wallet-icon wallet-icon-fallback">
        {name.charAt(0).toUpperCase()}
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Connect Wallet">
      <div className="wallet-selector">
        {connectionState !== 'idle' && connectionState !== 'error' ? (
          <div className="wallet-selector-status">
            <div className="wallet-selector-spinner" />
            <p>{getStatusText()}</p>
            {selectedWallet && (
              <p className="wallet-selector-selected">
                <WalletIcon icon={selectedWallet.info.icon} name={selectedWallet.info.name} />
                {selectedWallet.info.name}
              </p>
            )}
          </div>
        ) : (
          <>
            {error && (
              <div className="wallet-selector-error">
                <p>{error}</p>
                <div className="wallet-selector-error-actions">
                  <button
                    className="btn btn-text"
                    onClick={() => {
                      setConnectionState('idle')
                      setError(null)
                    }}
                  >
                    Try again
                  </button>
                  <button
                    className="btn btn-text"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {wallets.length === 0 ? (
              <div className="wallet-selector-empty">
                <p>No wallets detected</p>
                <p className="text-secondary">
                  Please install a browser wallet extension like MetaMask, Rabby, or Coinbase Wallet.
                </p>
              </div>
            ) : (
              <div className="wallet-selector-list">
                {wallets.map((wallet) => (
                  <button
                    key={wallet.info.uuid}
                    className="wallet-selector-item"
                    onClick={() => handleConnect(wallet)}
                    disabled={connectionState !== 'idle' && connectionState !== 'error'}
                  >
                    <WalletIcon icon={wallet.info.icon} name={wallet.info.name} />
                    <span className="wallet-selector-name">{wallet.info.name}</span>
                    {wallet.isLegacy && (
                      <span className="wallet-selector-badge">Legacy</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <div className="wallet-selector-footer">
          <p className="text-secondary">
            Signing in does not trigger a transaction or cost any gas.
          </p>
        </div>
      </div>
    </Modal>
  )
}
